import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';

import { calculatePackagePrice } from 'services/api/pay';
import { find } from 'lodash-es';

import { PackageCheckboxProps, PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';

export interface PackageDetailProps {
  orgId: number;
  packageInfo: Pay.PackageInfoV2;
  currency: General.Currency;
  isEnterprise: boolean;
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setDurationNum: React.Dispatch<React.SetStateAction<Pay.DurationNum>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  onPackageChange?: PackageCheckboxProps['onChange'];
  onCalculatePackageChange?: (pkg?: Pay.CalculatePackagePriceRst) => void;
}

function PackageDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    setDurationNum,
    setCanOrder,
    setIsEnterprise,
    orgId,
    currency,
    isEnterprise,
    onPackageChange,
    onCalculatePackageChange,
  } = props;
  const { packageData, recommendData } = packageInfo;

  const { recommendPackageId, recommendDurationId } = recommendData;
  // 选中的套餐
  const [chkPackage, setChkPackage] = useState<Pay.PackageId | undefined>(recommendPackageId);
  // 选中的套餐时长
  const [chkDuration, setChkDuration] = useState(recommendDurationId);

  // 选中套餐的时长
  const durationDesc = useMemo(() => {
    const checkedPackage = find(packageData, ['id', chkPackage]);
    return checkedPackage?.durationDesc ?? [];
  }, [packageData, chkPackage]);

  // 重新计算价格
  useEffect(() => {
    const checkedDuration = find(durationDesc, ['id', chkDuration]);
    if (checkedDuration && chkPackage) {
      const { durationNum } = checkedDuration;
      calculatePackagePrice({
        orgId,
        orderType: 'ORDER',
        durationNum,
        newPackageId: chkPackage,
        currency,
      })
        .then((d) => {
          setDurationNum(durationNum);
          setCanOrder(true);
          onCalculatePackageChange?.(d);
        })
        .catch((e) => {
          setCanOrder(false);
          onCalculatePackageChange?.(undefined);
          message.error(`/calculatePackagePrice ${e?.message}`);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chkDuration, chkPackage, currency, durationDesc]);

  return (
    <>
      <PackageSelectPanel
        value={chkPackage}
        recommendPackageId={recommendPackageId}
        list={packageData}
        isEnterprise={isEnterprise}
        onChange={(isEnterprise, selected) => {
          if (isEnterprise) {
            // 企业版
            setIsEnterprise(true);
            setChkPackage(undefined);
          } else if (selected) {
            const { accountNum, id } = selected;
            setAccountNum(accountNum);
            setChkPackage(id);
            setChkDuration(recommendDurationId);
            setIsEnterprise(false);
          }
          onPackageChange?.(isEnterprise, selected);
        }}
      />
      {durationDesc.length ? (
        <DurationSelectPanel
          durationId={chkDuration}
          packageId={chkPackage}
          currency={currency}
          list={durationDesc}
          packageData={packageData}
          recommendDurationId={recommendDurationId}
          onChange={({ durationNum, id }) => {
            setDurationNum(durationNum);
            setChkDuration(id);
          }}
        />
      ) : null}
    </>
  );
}

export default PackageDetail;
